import { useContext, useEffect, useRef, useState } from "react";
import styles from "./styles/PosShoppingCart.module.css";
import Button from "../../../components/ui/button/Button";
import { useTranslation } from "react-i18next";
import OutputWindow from "../../../components/ui/output_window/OutputWindow";
import { removeWhiteSpace, stringTrim } from "../../../utils/utils";
import DataContext from "../../../context/DataContext";
import PosCashForm from "./PosCashForm";
import POSDataContext from "../../../context/POSDataContext";
import PosCardForm from "./PosCardForm";
import PosChequeForm from "./PosChequeForm";
import InputNumber from "../../../components/ui/input_number/InputNumber";
import useCurrencySymbol from "../../../hooks/useCurrencySymbol";
import useNumberFormat from "../../../hooks/useNumberFormat";

const CASH_REFERENCE = "cash customer";
const MONEY_ORDER_PREFIX = "MO#";
const BANK_NAME = "Bank";
// The default buttons object
const BUTTONS = [
    {
        body: "cash",
        value: true,
    },
    {
        body: "card",
        value: false,
    },
    {
        body: "check",
        value: false,
    },
];
const CARD_TYPES = ["Visa", "Master Card"]; // Card types used

// Default form input
const DEFAULT_FORM_INPUT = {
    method: "cash",
    amount: null,
    last4Digits: "",
    expiryDate: "",

    cashNotes: "",
    cashReference: "",

    authorizationCode: "",
    cardType: CARD_TYPES[0],
    bankName: "",
    chequeNumber: "",
    chequePostDate: "",
    isMoneyOrder: false,
    // chequePostDate: "",
    comment: "",
};
/**
 * Payment component
 * The component is using 3 forms, one for each payment method
 */
const PosPayment = ({
    setShowPayment, // {Function} - Function to set the visibility state of the payment modal
    calculateAmountToPay, // {Function} - Function to calculate the amount to pay
}) => {
    const { t } = useTranslation();
    const amountRef = useRef(null);
    const { currentCustom, connection } = useContext(DataContext);
    const { setPayments, payments } = useContext(POSDataContext);
    const [buttons, setButtons] = useState(BUTTONS);
    const [formInput, setformInput] = useState(DEFAULT_FORM_INPUT);

    const [formButtonDisable, setFormButtonDisable] = useState(true);
    // const [isMoneyOrder, setIsMoneyOrder] = useState(false);
    const [hasCashPayment, setHasCashPayment] = useState(false);
    const [hoverFlag, setHoverFlag] = useState(false);
    const currency = useCurrencySymbol(true);
    const formatNum = useNumberFormat();

    useEffect(() => {
        if (!connection.mixedPayments) {
            formInput.amount = calculateAmountToPay();
        }
    }, []);

    useEffect(() => {
        const today = new Date();
        const todayDate = today.toISOString().split("T")[0];
        if (formInput.method === "check") {
            setformInput((prev) => ({ ...prev, chequePostDate: todayDate }));
        }
    }, [formInput?.method]);

    // the amount required red notification highlight is set in the input element
    // and the formButtonDisable is not used for it

    // Set form button diasble state (Add payment button in the payment modal)
    // the button will be disabled if amount to pay is larger than the amount paid
    // or if the amount is 0 or if one of the required fields are not filled
    useEffect(() => {
        setFormButtonDisable(
            calculateAmountToPay() < parseFloat(formInput?.amount) ||
                !parseFloat(formInput?.amount) ||
                validateFormFields(),
        );
    }, [formInput]);

    // Ceck if there is any cash payments avialable
    useEffect(() => {
        setHasCashPayment(
            payments.some((item) => item.PaymentMethod === "cash"),
        );
    }, [payments]);

    // Set the default button and method to card if there is any cash payment
    useEffect(() => {
        if (hasCashPayment) {
            setformInput((prevInputs) => ({ ...prevInputs, method: "card" }));
            setButtons((prevButtons) =>
                prevButtons.map((button, i) => ({
                    ...button,
                    value: button?.body === "card" ? true : false,
                })),
            );
        }
    }, [hasCashPayment]);

    // set the focus on the amount input
    useEffect(() => {
        amountRef.current && amountRef.current.focus();
    }, []);

    useEffect(() => {
        amountRef.current && amountRef.current.focus();
    }, []);

    /**
     * Validate the from inputs depending on the payment method
     * @returns {Boolean} - The form inputs validiation
     */
    const validateFormFields = () => {
        // if (showTab("cash")) return false;
        if (showTab("cash"))
            // const currentInput =
            return formInput?.isMoneyOrder
                ? !stringTrim(
                      formInput?.cashReference.slice(MONEY_ORDER_PREFIX.length),
                  )
                : false;

        if (showTab("card")) {
            return !(
                formInput?.authorizationCode &&
                formInput?.last4Digits &&
                formInput?.last4Digits?.length === 4 &&
                formInput?.expiryDate
            );
        }

        if (showTab("check"))
            return !(
                formInput?.chequeNumber &&
                // formInput?.bankName &&
                formInput?.chequePostDate
            );
    };

    /**
     * Payment type handler function
     * Reset the input fields with keeping the amount
     * Set the payment method to the selected one
     * Activate the payment button
     * @param {Integer} index - The index of the button
     * @param {Event} event - The event object
     */
    const handletype = (index, event) => {
        event.preventDefault();
        setformInput((prevInput) => ({
            ...DEFAULT_FORM_INPUT,
            amount: prevInput.amount,
        })); // reset the fields except the amount

        handleChange({
            target: {
                name: "method",
                value: event.target.value
                    ? event.target.value.toLowerCase()
                    : "",
            },
        }); // set the payment method
        amountRef.current && amountRef.current.focus();
        setButtons((prevButtons) =>
            prevButtons.map((button, i) => ({
                ...button,
                value: i === index,
            })),
        ); // set the current payment button to active
    };

    /**
     * Change handler function
     * @param {Event} event - The event object
     */
    const handleChange = (event) => {
        /**
         * format the last 4 digit input by excluding non numbers and white space from it
         * @param {String} value - the input of the card last 4 digit
         * @returns {String} The string excluding non numbers and spaces
         */
        const formatLast4 = (value) => {
            const lastChar = value.slice(-1);
            if (!isNaN(lastChar)) return removeWhiteSpace(value);
        };

        setformInput((prevformInput) => ({
            ...prevformInput,
            [event.target.name]:
                event.target.type === "checkbox"
                    ? event.target.checked
                    : event.target.name === "last4Digits" // only if the input is for the last 4 digits accept numbers only
                      ? formatLast4(event.target.value)
                      : event.target.value,
        }));
    };

    /**
     * return the window content for the amount remaining to pay
     * @returns {JSX.Element} - The remaining amount to pay
     */
    const getWindowContnent = () => {
        return (
            <h3
                className={`${styles.payment_remaining_amount_container} flex-container`}
            >
                <span className={`${styles.payment_output_label}`}>
                    {`${t("remaining amount")} :`}
                </span>
                <span className={`${styles.payment_output_amount}`}>
                    {currency}
                    {formatNum(calculateAmountToPay())}
                </span>
            </h3>
        );
    };

    /**
     * Save the payments to the locsal storage
     * @param {Event} event - The event object
     */
    const handleSubmitLocal = (event) => {
        event.preventDefault();
        if (formButtonDisable) return;
        const maxId =
            payments && payments.length
                ? payments.reduce(
                      (max, item) => (item.id > max ? item.id : max),
                      -Infinity,
                  )
                : 0; // find the maximum id for the avialable payments
        // creating the payload depending on the payment method
        // The payload will be used later for submitting the payments to the backend
        let payLoad = {};

        switch (formInput?.method) {
            case "cash":
                payLoad = {
                    id: maxId + 1,
                    PaymentMethod: "cash",
                    amount: formInput?.amount,
                    comment: formInput?.comment,
                    cashReference: stringTrim(formInput?.cashReference)
                        ? stringTrim(formInput?.cashReference)
                        : CASH_REFERENCE,
                };
                break;
            case "card":
                payLoad = {
                    id: maxId + 1,
                    expiryDate: formInput?.expiryDate,
                    PaymentMethod: "credit card",
                    amount: formInput?.amount,
                    last4Digits: formInput?.last4Digits,
                    authorizationCode: formInput?.authorizationCode,
                    cardType: formInput?.cardType,
                    comment: formInput?.comment,
                };
                break;
            case "check":
                payLoad = {
                    id: maxId + 1,

                    PaymentMethod: "cheque",
                    amount: formInput?.amount,
                    bankName: stringTrim(formInput?.bankName)
                        ? stringTrim(formInput?.bankName)
                        : BANK_NAME,
                    chequeNumber: formInput?.chequeNumber,

                    chequePostDate: formInput?.chequePostDate,

                    comment: formInput?.comment,
                };
                break;
            default:
                return;
        }

        setPayments((prevPayments) => {
            if (prevPayments && prevPayments.length) {
                return [...prevPayments, payLoad];
            } else {
                return [payLoad];
            }
        }); // add a new payment
        setShowPayment(false);
    };

    /**
     * Cancel payment handle function
     */
    const handleCancel = () => {
        setformInput(DEFAULT_FORM_INPUT);
        setShowPayment(false);
    };
    /**
     * Check if the cuurent tab name is the same as the one provided
     * @param {String} tabName - The form name to check
     * @returns {Bolean} - the validation result
     */
    const showTab = (tabName) => {
        const currentTab = buttons.filter((button) => button?.value === true);
        return currentTab[0]?.body === tabName;
    };

    return (
        <section className={`${styles.payment_form_back} flex-container`}>
            <div
                className={`${styles.main_form_container} ${styles.payment_form} fade-in v-flex-container`}
            >
                <form
                    className={`${styles.form_container} v-flex-container`}
                    onSubmit={handleSubmitLocal}
                    noValidate={true} // Disable default HTML5 validation
                >
                    <div
                        className={`${styles.payment_type_buttons_container} flex-container`}
                    >
                        {buttons.map((button, idx) => (
                            <div
                                key={idx}
                                className={`${styles.payment_type_button_container} flex-container`}
                            >
                                <Button
                                    body={t(button?.body)}
                                    size={"adapt"}
                                    type={"button"}
                                    fullWidth={true}
                                    active={button?.value}
                                    onClickFuction={(event) =>
                                        handletype(idx, event)
                                    }
                                    // disable the cash button if there is a previous cash payment
                                    disableHTML={
                                        button?.body === "cash" &&
                                        hasCashPayment
                                    }
                                    disabled={
                                        button?.body === "cash" &&
                                        hasCashPayment
                                    }
                                />
                            </div>
                        ))}
                    </div>

                    <div
                        className={`${styles.input_group_container}  v-flex-container`}
                    >
                        <div
                            className={` ${styles.payment_number} v-flex-container`}
                        >
                            <h4>{t("amount to pay")}</h4>
                            {/* The amount's input */}
                            <InputNumber
                                ref={amountRef}
                                inputId={"amount"}
                                inputValue={formInput?.amount}
                                handleChangeFunction={handleChange}
                                required={
                                    hoverFlag &&
                                    (calculateAmountToPay() <
                                        parseFloat(formInput?.amount) ||
                                        !parseFloat(formInput?.amount))
                                } // The amount should not be more than the amount left to pay
                                // and should be more than 0

                                // required={
                                //     hoverFlag &&
                                //     (connection?.mixedPayments
                                //         ? calculateAmountToPay() <
                                //               parseFloat(formInput?.amount) ||
                                //           !parseFloat(formInput?.amount)
                                //         : calculateAmountToPay() >
                                //               parseFloat(formInput?.amount) ||
                                //           !parseFloat(formInput?.amount))
                                // }

                                disable={!connection?.mixedPayments}
                                placeholder={t("required")}
                                textAlign={"center"}
                                maxValue={calculateAmountToPay()} // the maximum value allowed to input
                                minValue={0.01}
                                step={0.01}
                                adaptFont={true}
                            />
                        </div>
                        {/* Show the corresponding form */}
                        {showTab("cash") ? (
                            <PosCashForm
                                MONEY_ORDER_PREFIX={MONEY_ORDER_PREFIX}
                                CASH_REFERENCE={CASH_REFERENCE}
                                formInput={formInput}
                                hoverFlag={hoverFlag}
                                handleChange={handleChange}
                            />
                        ) : showTab("card") ? (
                            <PosCardForm
                                formInput={formInput}
                                hoverFlag={hoverFlag}
                                handleChange={handleChange}
                                cardTypes={CARD_TYPES}
                            />
                        ) : showTab("check") ? (
                            <PosChequeForm
                                BANK_NAME={BANK_NAME}
                                formInput={formInput}
                                hoverFlag={hoverFlag}
                                handleChange={handleChange}
                            />
                        ) : (
                            <></>
                        )}
                    </div>

                    <div
                        className={`${styles.payment_output_window_container} ${styles.remaining_window_container} flex-container`}
                    >
                        <OutputWindow
                            color={currentCustom?.color?.current}
                            content={getWindowContnent()} // the amount left to pay
                            padding={" 1em"}
                            fullWidth={true}
                        />
                    </div>
                    <div
                        className={`${styles.form_button_container} ${styles.payment_button} flex-container`}
                    >
                        <Button
                            body={t(`add payment`)}
                            type={"submit"}
                            disabled={formButtonDisable}
                            color={"green"}
                            setHoverFlag={setHoverFlag}
                            size={"adapt"}
                        />
                        <Button
                            body={t(`cancel`)}
                            type={"button"}
                            color={"red"}
                            onClickFuction={handleCancel}
                        />
                    </div>
                </form>
            </div>
        </section>
    );
};

export default PosPayment;
